import {
  OrderResponseDto,
  RailVisitResponseDto,
  TruckVisitDto,
  VesselVisitDto,
} from '@planning/app/api'
import { SimpleListStore } from '@planning/components/list/SimpleListStore'
import { EventTypes } from '@planning/messages/eventsTypes'
import { ICreateGeneralCargoOrderFormData } from '@planning/pages/Order/components/CreateGeneralCargoOrderPageForm'
import { CompanyItemStore } from '@planning/rt-stores/externalDriver/CompanyItemStore'
import { RailVisitItemStore } from '@planning/rt-stores/railVisit/RailVisitItemStore'
import { TruckVisitItemStore } from '@planning/rt-stores/truckVisit/TruckVisitItemStore'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import generalCargoService from '@planning/services/generalCargoService'
import packageService from '@planning/services/packageService'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { GetOrderDamagesAmountFunc, OrderDamageDto } from './OrderDamageDto'
import { PackageDto } from './PackageDto'

export class GeneralCargoViewStore {
  orders: OrderResponseDto[] = []
  packages: PackageDto[] = []
  commodityPackages: number[] = []
  selectedOrder?: OrderResponseDto
  orderFormData?: ICreateGeneralCargoOrderFormData

  getOrderDamagesAmountByOrderIds?: GetOrderDamagesAmountFunc
  damageOrders: OrderDamageDto[] = []

  generalCargoOrdersStore = new SimpleListStore<OrderResponseDto>(async () => {
    const result = await generalCargoService.getAll()
    await this.fetchRelatedEntities(result)

    if (this.getOrderDamagesAmountByOrderIds) {
      this.damageOrders = await this.getOrderDamagesAmountByOrderIds(result.map(order => order.id))
    }

    return result
  })

  constructor(
    messageBus: IMessageBus,
    private readonly vesselVisitItemStore: VesselVisitItemStore,
    private readonly truckVisitItemStore: TruckVisitItemStore,
    private readonly railVisitItemStore: RailVisitItemStore,
    private readonly companyItemStore: CompanyItemStore,
  ) {
    makeObservable(this, {
      selectedOrder: observable,
      orders: observable,
      packages: observable,
      commodityPackages: observable,
      damageOrders: observable,

      setDamageOrders: action,
      getPackages: action,
      setCommodityPackages: action,
      receivePackageDeletedMessage: action,
      receivePackageUpsertedMessage: action,

      packagesOptions: computed,
      carrierVisits: computed,
    })

    messageBus.subscribeEvent(EventTypes.PackageDeleted, this.receivePackageDeletedMessage)
    messageBus.subscribeEvent(EventTypes.PackageUpserted, this.receivePackageUpsertedMessage)
    messageBus.subscribeEvent(EventTypes.OrdersUpsertedEvent, this.receiveOrdersUpsertedMessage)
  }

  selectOrder = (order?: OrderResponseDto) => {
    this.selectedOrder = order
    this.setCommodityPackages([])
  }

  setOrderFormData = (data: ICreateGeneralCargoOrderFormData) => (this.orderFormData = data)

  getPackages = async () => {
    this.packages = await packageService.getAll()
  }

  loadAllOrders = async () => {
    const result = await generalCargoService.getAll()
    await this.fetchRelatedEntities(result)

    if (this.getOrderDamagesAmountByOrderIds) {
      this.damageOrders = await this.getOrderDamagesAmountByOrderIds(result.map(order => order.id))
    }

    runInAction(() => {
      this.generalCargoOrdersStore.items = result
      this.generalCargoOrdersStore.isNoMatches = !result.length
    })
  }

  setCommodityPackages = (commodityPackages: number[]) => {
    this.commodityPackages = [...commodityPackages]
  }

  async fetchRelatedEntities(orders: OrderResponseDto[]) {
    const groupedIds = this.getCarrierVisitIdsByTypeFromOrders(orders)
    const customerIds = orders
      .map(order => order.customerId)
      .filter((id): id is number => typeof id === 'number')

    if (groupedIds.vessel.length) {
      await this.vesselVisitItemStore.fetchByIds(groupedIds.vessel)
    }

    if (groupedIds.train.length) {
      await this.railVisitItemStore.fetchByIds(groupedIds.train)
    }

    if (groupedIds.truck.length) {
      await this.truckVisitItemStore.fetchByIds(groupedIds.truck)
    }

    if (customerIds.length) {
      await this.companyItemStore.fetchByIds(customerIds)
    }
  }

  setDamageOrders = (damages: OrderDamageDto[]) => {
    this.damageOrders = damages
  }

  get packagesOptions() {
    return _(
      this.packages.map(x => ({
        ...x,
        assignedToCommodity: this.commodityPackages.includes(x.id) ? 'suggested' : 'others',
      })),
    )
      .orderBy(x => x.assignedToCommodity, 'desc')
      .value()
  }

  get carrierVisits(): (VesselVisitDto | TruckVisitDto | RailVisitResponseDto)[] {
    const groupedIds = this.getCarrierVisitIdsByTypeFromOrders(this.generalCargoOrdersStore.items)

    const carrierVisitData = []

    if (groupedIds.vessel.length) {
      const data = _.values(this.vesselVisitItemStore.elements)
        .filter(v => groupedIds.vessel.includes(v.id))
        .map(v => v.data)
      carrierVisitData.push(...data)
    }

    if (groupedIds.train.length) {
      const data = _.values(this.railVisitItemStore.elements)
        .filter(r => groupedIds.train.includes(r.id))
        .map(r => r.data)
      carrierVisitData.push(...data)
    }

    if (groupedIds.truck.length) {
      const data = _.values(this.truckVisitItemStore.elements)
        .filter(t => groupedIds.truck.includes(t.id))
        .map(t => t.data)
      carrierVisitData.push(...data)
    }

    return carrierVisitData
  }

  receivePackageUpsertedMessage = (event: IEvent<PackageDto>): void => {
    const packageDto = event.payload
    this.packages = [...this.packages.filter(p => packageDto.id !== p.id), packageDto]
  }

  receivePackageDeletedMessage = (event: IEvent<[{ id: number }]>): void => {
    const ids = event.payload.map(p => p.id)
    this.packages = this.packages.filter(p => !ids.includes(p.id))
  }

  receiveOrdersUpsertedMessage = (event: IEvent<OrderResponseDto[]>): void => {
    const orders = event.payload.filter(o =>
      this.generalCargoOrdersStore.items.some(go => go.id === o.id),
    )

    if (orders.length) {
      runInAction(() => {
        this.generalCargoOrdersStore.items = this.generalCargoOrdersStore.items.map(go => {
          const updatedOrder = orders.find(o => o.id === go.id)
          return updatedOrder ? updatedOrder : go
        })
      })
    }
  }

  private getCarrierVisitIdsByTypeFromOrders(orders: OrderResponseDto[]) {
    const groupedIds: { [key: string]: number[] } = {
      vessel: [],
      truck: [],
      train: [],
    }

    orders.forEach((obj: OrderResponseDto) => {
      switch (obj.carrierVisitType) {
        case 'Vessel':
          if (obj.carrierVisitId !== null) groupedIds.vessel.push(obj.carrierVisitId!)
          break
        case 'Truck':
          if (obj.carrierVisitId !== null) groupedIds.truck.push(obj.carrierVisitId!)
          break
        case 'Train':
          if (obj.carrierVisitId !== null) groupedIds.train.push(obj.carrierVisitId!)
          break
        default:
          break
      }
    })

    return groupedIds
  }
}
